import type { INotification } from '@pulse/shared';
import { useEffect, useRef } from 'react';

// EventSource (not fetch/apiFetch) — SSE is a long-lived GET the browser manages natively,
// including auto-reconnect on drop; `withCredentials` is what sends the httpOnly auth cookie,
// the same requirement `apiFetch`'s `credentials: 'include'` covers for regular requests.
// The callbacks are kept in refs so a caller passing fresh inline functions each render
// doesn't tear down and reopen the connection — only mount/unmount should do that.
//
// `onReconnect` fires on every `open` event after the first — i.e. only when the browser has
// re-established a connection that had previously dropped (server restart, network blip, ...).
// Any notification fired while disconnected never arrives on its own (EventSource doesn't
// replay history), so a caller should use this to re-sync its baseline (re-fetch the list /
// unread count) rather than trust the stream alone.
export function useNotificationStream(
  onNotification: (notification: INotification) => void,
  onReconnect?: () => void,
): void {
  const callbackRef = useRef(onNotification);
  callbackRef.current = onNotification;
  const reconnectRef = useRef(onReconnect);
  reconnectRef.current = onReconnect;

  useEffect(() => {
    const source = new EventSource('/api/v1/notifications/stream', { withCredentials: true });
    let hasConnectedBefore = false;

    const handleNotification = (event: MessageEvent<string>) => {
      callbackRef.current(JSON.parse(event.data) as INotification);
    };
    const handleOpen = () => {
      if (hasConnectedBefore) {
        reconnectRef.current?.();
      }
      hasConnectedBefore = true;
    };

    source.addEventListener('notification', handleNotification);
    source.addEventListener('open', handleOpen);

    return () => {
      source.removeEventListener('notification', handleNotification);
      source.removeEventListener('open', handleOpen);
      source.close();
    };
  }, []);
}
